import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { NumberParam, StringParam, useQueryParam } from "use-query-params";

import { Pagination } from "src/components/Pagination";
import { FirebaseDataService } from "src/services/Firebase/data";
import { ClientSpecificLocationForTable, ID } from "src/types";

import { Container, EmptyContainer, NoTicketsMessage, PaginationContainer } from "../../../components/ListScreen";
import { compareBy } from "../../../utils";
import { LocationsTable } from "./LocationsTable";
import {
  CLIENTS_LOCATIONS_BREADCRUMBS,
  DEFAULT_PAGE_SIZE,
  INITIAL_LAST_PAGE,
  LocationParams,
  LOCATIONS_TABLE_HEADERS,
  MANAGER_CLIENTS_LOCATIONS_BREADCRUMBS,
} from "./constants";
import { Content, ContentFrame, TopBar } from "src/styles";
import BreadCrumbs from "src/components/BreadCrumbs";
import { generateBreadcrumbs } from "src/helpers/breadcrumbs";
import { useSelector } from "react-redux";
import { getUserData } from "src/redux/selectors/user";
import Button from "src/components/Button";
import { faDownload } from "@fortawesome/pro-light-svg-icons";
import ExportDateRangeModal from "src/components/organisms/ExportDateRangeModal";
import { ExportDateRange } from "src/components/organisms/ExportDateRangeModal/types";
import { CreateButtonWrapper, SearchHeaderContainer } from "./components/styles";
import { SearchInputWrapper } from "src/components/atoms/SearchInput/styles";
import LocationSearch from "./components/LocationSearch";
import { filterActiveLocations } from "src/utils/location";

export const Locations: React.FC<LocationParams> = ({ clientId }: LocationParams) => {
  const navigate = useNavigate();

  const { isClient, clientId: userClientId } = useSelector(getUserData);

  const [locations, setLocations] = useState<ClientSpecificLocationForTable[]>([]);
  const [currentLocationsPage, setCurrentLocationsPage] = useState<ClientSpecificLocationForTable[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  const [openedLocationsPage, setOpenedLocationsPage] = useQueryParam("openedLocationsPage", NumberParam);
  const [lastPage, setLastPage] = useState<number>(INITIAL_LAST_PAGE);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExportingTimeAndAttendance, setIsExportingTimeAndAttendance] = useState(false);
  const [searchInput, setSearchInput] = useState<string>("");
  const [appliedSearch, setAppliedSearch] = useQueryParam("locationAppliedSearch", StringParam);
  const [searchText, setSearchText] = useQueryParam("locationSearchText", StringParam);

  const pageNum = openedLocationsPage || 1;
  const resolvedClientId = clientId?.toString() || userClientId?.toString() || "";

  const getPageSize = async () => {
    try {
      const { data } = await FirebaseDataService.getEmployeeLocationsPageSize();
      setPageSize(data || DEFAULT_PAGE_SIZE);
    } catch (e) {
      setPageSize(DEFAULT_PAGE_SIZE);
    }
  };

  const getLocations = async (searchKeyword?: string) => {
    setIsLoading(true);
    if (resolvedClientId && (!lastPage || (lastPage && pageNum <= lastPage))) {
      try {
        const { data: locationsArray } = await FirebaseDataService.getLocationsByClientId(resolvedClientId);
        const activeLocations = filterActiveLocations(locationsArray || []);

        const sortedLocations = activeLocations.sort(
          (location1: ClientSpecificLocationForTable, location2: ClientSpecificLocationForTable) =>
            compareBy("label", location1, location2),
        );

        const keyword = searchKeyword?.trim().toLowerCase();
        const filteredLocations = keyword
          ? sortedLocations.filter((loc: ClientSpecificLocationForTable) => {
              const searchableText = [loc.label, loc.address, loc.assignedSupervisorName]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
              return searchableText.includes(keyword);
            })
          : sortedLocations;

        const currentPage = openedLocationsPage || 1;
        const locationsPageArray = filteredLocations.slice((currentPage - 1) * pageSize, currentPage * pageSize);

        setLocations(filteredLocations);
        setCurrentLocationsPage(locationsPageArray);
        setLastPage(Math.ceil(filteredLocations.length / pageSize));
      } catch (e) {
        console.log(e.message);
      }
    }

    setIsLoading(false);
  };

  const onClickApplySearch = async () => {
    setAppliedSearch("true");
    setOpenedLocationsPage(1);
    setSearchText(searchInput);
    await getLocations(searchInput);
  };

  const onClickCancelSearch = () => {
    setAppliedSearch(null);
    setSearchInput("");
    setSearchText(null);
  };

  const onClickClearSearch = async () => {
    setAppliedSearch(null);
    setSearchInput("");
    setSearchText(null);
    setOpenedLocationsPage(1);
    await getLocations("");
  };

  const onPageChangeClick = (pageNumber: number) => {
    setOpenedLocationsPage(pageNumber);
    setCurrentLocationsPage(locations.slice((pageNumber - 1) * pageSize, pageNumber * pageSize));
  };

  const onRowClick = (id: ID, label: string) => {
    navigate(`/time-and-attendance/${id}`, { state: { locationLabel: label } });
  };

  useEffect(() => {
    getPageSize().finally(() => getLocations(searchText ?? undefined));
  }, [pageSize, resolvedClientId]);

  const onClickBreadcrumb = (route: ID | null) => typeof route === "string" && navigate(route);

  const onExportButtonClick = () => {
    setIsExportModalOpen(true);
  };

  const onSubmitExportDateRange = async ({ fromDate, toDate }: ExportDateRange): Promise<boolean> => {
    setIsExportingTimeAndAttendance(true);
    try {
      if (resolvedClientId) {
        const response = await FirebaseDataService.exportTimeAndAttendanceLogs(
          resolvedClientId,
          undefined,
          fromDate,
          toDate,
        );
        const { fileData, fileName, mimeType } = response.data;

        const byteCharacters = atob(fileData);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        link.click();
        window.URL.revokeObjectURL(url);

        alert("File exported successfully");
        return true;
      }

      alert("Unable to export time and attendance logs for this client.");
      return false;
    } catch (error) {
      console.error("Error exporting time and attendance logs:", error);
      alert("An error occurred while exporting time and attendance logs. Please try again.");
      return false;
    } finally {
      setIsExportingTimeAndAttendance(false);
    }
  };

  return (
    <Container>
      <TopBar>
        <BreadCrumbs
          folderStack={
            !isClient
              ? generateBreadcrumbs(MANAGER_CLIENTS_LOCATIONS_BREADCRUMBS, {
                  id: (clientId && clientId.toString()) || "id",
                  name: (clientId && clientId.toString()) || "name",
                })
              : generateBreadcrumbs(CLIENTS_LOCATIONS_BREADCRUMBS, {
                  id: (userClientId && userClientId.toString()) || "id",
                  name: (userClientId && userClientId.toString()) || "name",
                })
          }
          onClickItem={onClickBreadcrumb}
        />
      </TopBar>

      <Content>
        <ContentFrame>
          <SearchHeaderContainer>
            <SearchInputWrapper>
              <LocationSearch
                value={searchInput}
                applied={!!appliedSearch}
                onChange={setSearchInput}
                onClickCancel={onClickCancelSearch}
                onClickApply={onClickApplySearch}
                onClickClear={onClickClearSearch}
              />
            </SearchInputWrapper>
            <CreateButtonWrapper>
              <Button
                text="Export Time and Attendance"
                variant="white"
                loading={isExportingTimeAndAttendance}
                disabled={isExportingTimeAndAttendance}
                additionalStyles={{ display: "flex" }}
                icon={faDownload}
                onClick={onExportButtonClick}
              />
            </CreateButtonWrapper>
          </SearchHeaderContainer>
          {isLoading ? (
            <EmptyContainer>
              <Spinner animation="border" size="sm" />
            </EmptyContainer>
          ) : (
            <>
              {currentLocationsPage?.length ? (
                <LocationsTable
                  onRowClick={onRowClick}
                  isLoading={isLoading}
                  headers={LOCATIONS_TABLE_HEADERS}
                  data={currentLocationsPage}
                />
              ) : (
                <NoTicketsMessage>No Locations assigned</NoTicketsMessage>
              )}
              <PaginationContainer>
                <Pagination
                  totalCount={locations.length}
                  pageSize={pageSize}
                  onPageChange={onPageChangeClick}
                  currentPage={pageNum}
                />
              </PaginationContainer>
            </>
          )}
        </ContentFrame>
      </Content>
      <ExportDateRangeModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onSubmit={onSubmitExportDateRange}
        title="Export Time and Attendance"
        confirmText="Export"
        isSubmitting={isExportingTimeAndAttendance}
      />
    </Container>
  );
};
